import { z } from 'zod';
import { Id } from './primitives.js';
/**
 * A role identifier.
 *
 * Roles are rows in `roles`, not an enum — ADR-0006. FR-13 names five of them
 * (SALES, SPG, SUPERVISOR, REGIONAL_MANAGER, ADMIN) and the migration seeds
 * exactly those, but an administrator may add, rename and delete roles at
 * runtime, so this contract constrains the SHAPE of a role code and nothing
 * else. Code that branches on a particular value of this type is wrong by
 * construction: the answer to "may this caller do X" is `Permission`.
 *
 * Upper snake case, because the code appears in audit rows and in the `role`
 * claim, and a free-form label there is a value nobody can grep for. The
 * human-readable name lives on the role row.
 */
export const RoleCode = z
    .string()
    .regex(/^[A-Z][A-Z0-9_]{1,63}$/, 'kode peran memakai HURUF_BESAR dan garis bawah, 2–64 karakter');
/**
 * Permission strings, mapped to roles in the database (TAD 4.6).
 *
 * Enforced at BOTH route level (NestJS guards) and query level (the mandatory
 * scope predicate in the repository base class). Route guards alone satisfy only
 * the menu-visibility reading of FR-13 and leak data through reporting and
 * export endpoints — TAD 4.5.
 */
export const Permission = z.enum([
    'outlet:read',
    'outlet:write',
    'sku:read',
    'sku:write',
    // There is no `planogram:*` pair. The planogram was its own master with its
    // own screen and its own permissions; its targets are SKU columns now, so what
    // guards them is `sku:write`, and the reports that read the resulting scores
    // are `report:read`, as they always were. Migration 0013 rebuilt the enum.
    'user:read',
    'user:write',
    'visit:create',
    'visit:read:own',
    'visit:read:region',
    'visit:read:all',
    'dashboard:read',
    'report:read',
    'report:export',
    'fraud:read',
    'fraud:disposition',
    'masterdata:import',
    /**
     * FR-14 Plan vs Actual targets.
     *
     * Their own pair rather than an overload of `outlet:write`, which every
     * REGIONAL_MANAGER already holds: reuse would silently grant "can set my
     * team's quota" to a role nobody decided should have it, and the permission
     * matrix — the visible answer to FR-13's "what can each role do" — would stop
     * describing reality.
     */
    'target:read',
    'target:write',
    /**
     * The operational health of the deployment itself — INF-04's console half.
     *
     * READ ONLY, AND THERE IS NO `system:write` TO PAIR WITH IT. That asymmetry is
     * the point: every other permission here guards business data, and the screen
     * this one opens guards nothing — it reports whether Postgres answered, whether
     * the worker is beating, how deep the queues are. Nothing behind it can be
     * changed from a browser, so a write half would be a permission with no verb.
     *
     * NOT a reuse of `user:read`, which is what the Administrasi nav group already
     * gates. That would tie "may see whether the photo pipeline is alive" to "may
     * administer people" — the same overload `settings.controller.ts` declined for
     * the price-index screen, and for the same reason: the permission matrix is
     * FR-13's visible answer to "what can each role do", and an overload makes it
     * describe something other than reality.
     *
     * It is deliberately absent from `CONSOLE_PERMISSIONS` in the manager console:
     * holding this alone should not make somebody a console user.
     */
    'system:read',
]);
/**
 * The caller's data entitlement, resolved at authentication time and applied as
 * a mandatory predicate on every query path (TAD 4.5 "Row-level access scoping").
 *
 * `regionIds` empty + `allRegions: false` means the caller sees only their own
 * assigned outlets.
 *
 * `role` is the caller's role CODE and it is descriptive only — it is what the
 * audit trail stamps into `actor_role` and what the UI badge renders. It is
 * deliberately not the role's id: an audit row must keep meaning after the role
 * it names has been renamed or deleted, which a foreign key cannot do. Nothing
 * in the authorisation path reads it; `permissions` is the decision.
 */
export const AccessScope = z.object({
    userId: Id,
    role: RoleCode,
    permissions: z.array(Permission),
    regionIds: z.array(Id),
    allRegions: z.boolean(),
});
//# sourceMappingURL=rbac.js.map